import {
    FilterFn,
    PaginationState,
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable
} from '@tanstack/react-table'

import { UsersIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
    UserView,
    useEditUserMutation,
    useGetUserTeamsAdminQuery,
    useSearchUsersCountQuery,
    useSearchUsersQuery,
    useSetUserToggleMutation
} from '../../api/client'
import AdminActionBar from '../../components/admin/AdminActionBar'
import PageMasthead from '../../components/PageMasthead'
import TeamFilter, { TeamFilterInitialSelection } from '../../components/content/search/TeamFilter'
import { toastError } from '../../components/Notification'
import SearchInput from '../../components/SearchInput'
import SimpleLoader from '../../components/SimpleLoader'
import Spinner from '../../components/Spinner'
import { fuzzyFilter } from '../../components/table/helpers'
import SimplePagination from '../../components/table/SimplePagination'
import SimpleTable from '../../components/table/SimpleTable'
import { RowActions, rowActionsHeader, ViewRowAction } from '../../components/table/rowActions'
import TeamBadges from '../../components/team/TeamBadges'
import { UserToggle } from '../../models/Toggles'

declare module '@tanstack/table-core' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
}

const columnHelper = createColumnHelper<UserView>()

type ToggleField = Exclude<keyof UserView['toggles'], '__typename'>

const snakeToCamel = (value: string) =>
    value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())

const formatToggleLabel = (value: string) =>
    value
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

function UserListPage() {
    const location = useLocation()

    const [globalFilter, setGlobalFilter] = useState('')
    const [data, setData] = useState<UserView[]>([])
    const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([])
    const [updatingField, setUpdatingField] = useState<string | null>(null)
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10
    })

    const pagination = React.useMemo(
        () => ({
            pageIndex,
            pageSize
        }),
        [pageIndex, pageSize]
    )

    const {
        data: getUsersData,
        loading: getUsersLoading,
        refetch: refetchUsers
    } = useSearchUsersQuery({
        variables: {
            params: {
                offset: pageIndex * pageSize,
                limit: pageSize,
                containsText: globalFilter,
                teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : null
            }
        }
    })

    const { data: getUsersCountData, refetch: refetchUsersCount } = useSearchUsersCountQuery({
        variables: {
            params: {
                containsText: globalFilter,
                teamIds: selectedTeamIds.length > 0 ? selectedTeamIds : null,
                // The offset and limit are not used for the count query but the query requires them.
                // We could create a different parameter type for the count query but this is quicker.
                offset: 0,
                limit: 0
            }
        }
    })

    const [editUserMutation] = useEditUserMutation()
    const [setUserToggleMutation] = useSetUserToggleMutation()

    let userCount = getUsersCountData?.searchUsersCount || 0

    const handlePermissionChange = async (user: UserView, field: string, value: boolean) => {
        const permissionKey = `${user.id}-${field}`
        setUpdatingField(permissionKey)

        try {
            await editUserMutation({
                variables: {
                    userParams: {
                        id: user.id,
                        emailAddress: user.username, // Assuming username is email
                        admin: field === 'admin' ? value : user.admin || false,
                        allowAccessOverride:
                            field === 'allowAccessOverride'
                                ? value
                                : user.allowAccessOverride || false,
                        canLike: field === 'canLikeContent' ? value : user.canLikeContent,
                        canReplyToContent:
                            field === 'canReplyToContent' ? value : user.canReplyToContent,
                        canUseReadStatus:
                            field === 'canUseReadStatus' ? value : user.canUseReadStatus,
                        dashboardEnabled:
                            field === 'dashboardEnabled' ? value : user.dashboardEnabled || false,
                        feedsEnabled: field === 'feedsEnabled' ? value : user.feedsEnabled
                        // selectedTeams is omitted for permission-only updates
                    }
                }
            })
            // Refetch data to update the UI
            await refetchUsers()
        } catch (error) {
            console.error('Error updating user permission:', error)
            toastError('Failed to update permissions')
        } finally {
            setUpdatingField(null)
        }
    }

    const handleUserToggleChange = async (user: UserView, toggle: UserToggle, value: boolean) => {
        const toggleKey = `${user.id}-toggle-${toggle}`
        setUpdatingField(toggleKey)

        try {
            await setUserToggleMutation({
                variables: {
                    userId: user.id,
                    toggleName: toggle,
                    value
                }
            })
            await refetchUsers()
        } catch (error) {
            console.error('Error updating user toggle:', error)
            toastError('Failed to update user toggle')
        } finally {
            setUpdatingField(null)
        }
    }

    function PermissionCheckbox({
        user,
        field,
        value,
        onChange
    }: {
        user: UserView
        field: string
        value: boolean
        onChange: (checked: boolean) => void
    }) {
        const permissionKey = `${user.id}-${field}`
        const isUpdating = updatingField === permissionKey

        if (isUpdating) {
            return (
                <div className="flex justify-center items-center w-5">
                    <SimpleLoader loading={true} />
                </div>
            )
        }

        return (
            <input
                type="checkbox"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                className="h-4 w-4 text-arwen-600 border-gray-300 rounded focus:ring-arwen-500"
                disabled={!!updatingField} // Disable all checkboxes while any update is in progress
            />
        )
    }

    function UserTeamsCell({ userId }: { userId: number }) {
        const { data: userTeamsData, loading: userTeamsLoading } = useGetUserTeamsAdminQuery({
            variables: { userId },
            skip: !userId
        })

        if (userTeamsLoading) {
            return (
                <div className=" flex animate-pulse flex-row items-stretch  h-full w-full space-x-1">
                    <div className=" bg-ink-200 h-4 rounded-sm w-20"></div>
                    <div className=" bg-ink-200 h-4 rounded-sm w-20"></div>
                </div>
            )
        }

        const teams = userTeamsData?.getUserTeamsAdmin || []

        return <TeamBadges teams={teams} maxVisible={2} size="sm" variant="compact" />
    }

    // When the location changes, e.g. navigating back from creating a new team, then we should refetch.
    useEffect(
        () => {
            refetchUsers()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.pathname]
    )

    useEffect(() => {
        setPagination({ pageIndex: 0, pageSize: 10 })
        refetchUsers()
        refetchUsersCount()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalFilter, selectedTeamIds])

    function ActionsCell(info: any) {
        const user = info.row.original as UserView
        return (
            <RowActions>
                <ViewRowAction href={`#/admin/user/edit/${user.id}`} label="View user" />
            </RowActions>
        )
    }

    const toggleConfigs = React.useMemo(
        () =>
            Object.values(UserToggle).map((toggle) => {
                const field = snakeToCamel(toggle) as ToggleField
                return {
                    toggle,
                    field,
                    label: formatToggleLabel(toggle)
                }
            }),
        []
    )

    const toggleColumns = toggleConfigs.map(({ toggle, field, label }) =>
        columnHelper.accessor(
            (user) => user.toggles?.[field] ?? false,
            {
                id: `toggle-${toggle}`,
                header: () => label,
                enableSorting: false,
                cell: (info) => (
                    <PermissionCheckbox
                        user={info.row.original}
                        field={`toggle-${toggle}`}
                        value={Boolean(info.getValue())}
                        onChange={(checked) =>
                            handleUserToggleChange(info.row.original, toggle, checked)
                        }
                    />
                )
            }
        )
    )

    const columns = [
        columnHelper.accessor('username', {
            header: () => 'User',
            enableSorting: false,
            cell: (info) => (
                // Square monogram tile, matching the profile list and the team list.
                <div className="flex items-center gap-2.5">
                    <span
                        className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-ink-100 text-[11px] font-bold text-ink-800"
                        aria-hidden="true"
                    >
                        {info.getValue().charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-ink-900">
                            {info.getValue()}
                        </div>
                        <div className="text-xs text-ink-500">ID: {info.row.original.id}</div>
                    </div>
                </div>
            )
        }),
        columnHelper.display({
            id: 'teams',
            header: () => 'Teams',
            cell: (info) => <UserTeamsCell userId={info.row.original.id} />
        }),
        columnHelper.accessor('canReplyToContent', {
            header: () => 'Reply',
            enableSorting: false,
            cell: (info) => (
                <PermissionCheckbox
                    user={info.row.original}
                    field="canReplyToContent"
                    value={info.getValue()}
                    onChange={(checked) =>
                        handlePermissionChange(
                            info.row.original,
                            'canReplyToContent',
                            checked
                        )
                    }
                />
            )
        }),
        columnHelper.accessor('canLikeContent', {
            header: () => 'Like',
            enableSorting: false,
            cell: (info) => (
                <PermissionCheckbox
                    user={info.row.original}
                    field="canLikeContent"
                    value={info.getValue()}
                    onChange={(checked) =>
                        handlePermissionChange(
                            info.row.original,
                            'canLikeContent',
                            checked
                        )
                    }
                />
            )
        }),
        columnHelper.accessor('canUseReadStatus', {
            header: () => 'Read/Unread',
            enableSorting: false,
            cell: (info) => (
                <PermissionCheckbox
                    user={info.row.original}
                    field="canUseReadStatus"
                    value={info.getValue()}
                    onChange={(checked) =>
                        handlePermissionChange(
                            info.row.original,
                            'canUseReadStatus',
                            checked
                        )
                    }
                />
            )
        }),
        columnHelper.accessor('feedsEnabled', {
            header: () => 'Feeds',
            enableSorting: false,
            cell: (info) => (
                <PermissionCheckbox
                    user={info.row.original}
                    field="feedsEnabled"
                    value={info.getValue()}
                    onChange={(checked) =>
                        handlePermissionChange(
                            info.row.original,
                            'feedsEnabled',
                            checked
                        )
                    }
                />
            )
        }),
        columnHelper.accessor('dashboardEnabled', {
            header: () => 'Reports',
            enableSorting: false,
            cell: (info) => (
                <PermissionCheckbox
                    user={info.row.original}
                    field="dashboardEnabled"
                    value={info.getValue() || false}
                    onChange={(checked) =>
                        handlePermissionChange(
                            info.row.original,
                            'dashboardEnabled',
                            checked
                        )
                    }
                />
            )
        }),
        ...toggleColumns,
        columnHelper.display({
            id: 'actions',
            header: rowActionsHeader,
            cell: (props) => <ActionsCell row={props.row} />
        })
    ]

    useEffect(() => {
        if (getUsersData?.searchUsers) {
            setData(getUsersData?.searchUsers)
        }
    }, [getUsersData])

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter
        },
        pageCount: Math.ceil(userCount / pageSize),
        state: {
            pagination
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        debugTable: false,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    })

    return (
        // Grey page backdrop behind the white list card, shared across admin lists.
        <div className="flex min-h-0 w-full flex-1 flex-col bg-gray-50">
            {/* Header */}
            <PageMasthead
                title="User management"
                tabTitle="User management"
                subtitle={`${data.length} user${data.length !== 1 ? 's' : ''} on this page${userCount > data.length ? ` of ${userCount} total` : ''}`}
                actions={
                    <a
                        href="#/admin/user/create"
                        className="rounded-lg bg-arwen-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-arwen-700"
                    >
                        Create user
                    </a>
                }
            />

            {/* Total users, previously shown as a quick stat in the header. */}
            <div className="flex items-center gap-1.5 px-6 pt-4 font-sans text-sm text-ink-500">
                <UsersIcon className="h-4 w-4" />
                <span>{userCount} total</span>
            </div>

            {/* Search and Filters */}
            <AdminActionBar>
                <div className="mr-4">
                    <TeamFilter
                        teamIds={selectedTeamIds}
                        onUpdateSelectedTeams={setSelectedTeamIds}
                        initialSelection={TeamFilterInitialSelection.None}
                    />
                </div>
                <div className="flex-1">
                    <SearchInput
                        keyword={globalFilter ?? ''}
                        onKeywordChange={(value) => setGlobalFilter(String(value))}
                        onSearch={(value) => setGlobalFilter(String(value))}
                    />
                </div>
            </AdminActionBar>

            {/* Content */}
            <div className="flex-1 px-6 py-4">
                {getUsersLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Spinner size={6} />
                    </div>
                ) : data.length === 0 ? (
                    <div className="text-center py-12">
                        <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2 text-sm text-gray-500">
                            {globalFilter || selectedTeamIds.length > 0
                                ? 'No users match your search criteria.'
                                : 'No users found.'}
                        </div>
                    </div>
                ) : (
                    <SimpleTable table={table} />
                )}

                {data.length > 0 && (
                    <div className="pt-3">
                        <SimplePagination table={table} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default UserListPage
